import React, { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { 
  Upload, 
  Download, 
  Users, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Plus,
  Trash2
} from "lucide-react";
import bulkStockTransferService from "../../services/BulkStockTransferService.js";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function BulkStockTransfer() {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.auth.user);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [manualEntries, setManualEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({ username: "", amount: "" });
  const [transferMode, setTransferMode] = useState("excel");

  const { data: downlineAgents, isLoading: agentsLoading } = useQuery({
    queryKey: ["downlineAgents"],
    queryFn: () => bulkStockTransferService.getDownlineAgents(),
  });

  const bulkTransferMutation = useMutation({
    mutationFn: (formData) => bulkStockTransferService.bulkStockTransfer(formData),
    onSuccess: (response) => {
      toast.success(t('bulkStockTransfer.successMessage'));
      
      if (response.data.failed > 0 && response.data.failedFile) {
        toast.info(t('bulkStockTransfer.failedTransfers', { count: response.data.failed }));
      }
      
      setSelectedFile(null);
      setManualEntries([]);
      setNewEntry({ username: "", amount: "" });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    onError: (error) => {
      console.error("Bulk transfer error:", error);
      toast.error(error?.response?.data?.error || t('bulkStockTransfer.errorMessage'));
    },
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type)) {
        toast.error(t('bulkStockTransfer.invalidFileType'));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('bulkStockTransfer.fileSizeError'));
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleManualAdd = () => {
    if (!newEntry.username || !newEntry.amount) {
      toast.error(t('bulkStockTransfer.requiredFields'));
      return;
    }

    if (parseFloat(newEntry.amount) <= 0) {
      toast.error(t('bulkStockTransfer.invalidAmount'));
      return;
    }

    if (manualEntries.some(entry => entry.username === newEntry.username)) {
      toast.error(t('bulkStockTransfer.duplicateUsername'));
      return;
    }

    setManualEntries(prev => [...prev, {
      ...newEntry,
      amount: parseFloat(newEntry.amount)
    }]);
    setNewEntry({ username: "", amount: "" });
  };

  const removeManualEntry = (index) => {
    setManualEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      if (transferMode === "excel") {
        if (!selectedFile) {
          toast.error(t('bulkStockTransfer.noFileSelected'));
          return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        
        bulkTransferMutation.mutate(formData);
        
      } else {
        if (manualEntries.length === 0) {
          toast.error(t('bulkStockTransfer.noEntries'));
          return;
        }

        const XLSX = await import('xlsx');
      
        const worksheetData = manualEntries.map(entry => ({
          username: entry.username,
          amount: entry.amount
        }));
      
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, t('bulkStockTransfer.worksheetName'));
      
        const excelBuffer = XLSX.write(workbook, { 
          bookType: 'xlsx', 
          type: 'array' 
        });
        
        const file = new File([excelBuffer], "manual_transfers.xlsx", { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });

        const formData = new FormData();
        formData.append("file", file);

        bulkTransferMutation.mutate(formData);
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(t('bulkStockTransfer.prepareDataError'));
    }
  };

  const downloadTemplate = async () => {
    try {
      const { utils, writeFile } = await import('xlsx');
      
      const templateData = [
        { username: "agent1", amount: 100 },
        { username: "agent2", amount: 200 },
        { username: "agent3", amount: 150 }
      ];
      
      const worksheet = utils.json_to_sheet(templateData);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, t('bulkStockTransfer.templateSheetName'));
      
      writeFile(workbook, t('bulkStockTransfer.templateFileName'));
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error(t('bulkStockTransfer.templateDownloadError'));
    }
  };

  const availableDownlines = downlineAgents?.data?.data || [];

  return (
    <div className="w-full px-5 py-4 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          {t('bulkStockTransfer.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          {t('bulkStockTransfer.subtitle')}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 dark:text-white">
          {t('bulkStockTransfer.transferMethod')}
        </h2>
        <div className="flex gap-4">
          <button
            onClick={() => setTransferMode("excel")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
              transferMode === "excel" 
                ? "bg-primary/10 text-primary border-primary/30 dark:bg-primary/20 dark:text-[#ff6b6b] dark:border-primary/40" 
                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
            }`}
          >
            <FileText size={20} />
            {t('bulkStockTransfer.excelUpload')}
          </button>
          <button
            onClick={() => setTransferMode("manual")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
              transferMode === "manual" 
                ? "bg-primary/10 text-primary border-primary/30 dark:bg-primary/20 dark:text-[#ff6b6b] dark:border-primary/40" 
                : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
            }`}
          >
            <Users size={20} />
            {t('bulkStockTransfer.manualEntry')}
          </button>
        </div>
      </div>

      {transferMode === "excel" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 dark:text-white">
            {t('bulkStockTransfer.uploadExcel')}
          </h2>
          
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center mb-4">
            <Upload className="mx-auto text-gray-400 dark:text-gray-500 mb-3" size={48} />
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              {selectedFile ? selectedFile.name : t('bulkStockTransfer.fileUploadPlaceholder')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('bulkStockTransfer.supportedFormats')}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-[#b10505] dark:bg-[#ff6b6b] dark:hover:bg-[#ff5252]"
            >
              {t('bulkStockTransfer.browseFiles')}
            </button>
          </div>

          {selectedFile && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-300">
                    {t('bulkStockTransfer.fileSelected')}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {selectedFile.name}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 text-primary dark:text-[#ff6b6b] border border-primary/30 dark:border-[#ff6b6b]/40 rounded-lg hover:bg-primary/5 dark:hover:bg-[#ff6b6b]/10"
            >
              <Download size={18} />
              {t('bulkStockTransfer.downloadTemplate')}
            </button>
          </div>
        </div>
      )}

      {transferMode === "manual" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 dark:text-white">
            {t('bulkStockTransfer.manualTransferEntries')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('bulkStockTransfer.usernameLabel')}
              </label>
              <input
                type="text"
                value={newEntry.username}
                onChange={(e) => setNewEntry(prev => ({ ...prev, username: e.target.value }))}
                placeholder={t('bulkStockTransfer.usernamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                list="downlineUsernames"
              />
              <datalist id="downlineUsernames">
                {availableDownlines.map(agent => (
                  <option key={agent.id} value={agent.username} />
                ))}
              </datalist>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('bulkStockTransfer.amountLabel')} ({t('bulkStockTransfer.currency')})
              </label>
              <input
                type="number"
                value={newEntry.amount}
                onChange={(e) => setNewEntry(prev => ({ ...prev, amount: e.target.value }))}
                placeholder={t('bulkStockTransfer.amountPlaceholder')}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleManualAdd}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
              >
                <Plus size={18} />
                {t('bulkStockTransfer.addEntry')}
              </button>
            </div>
          </div>

          {manualEntries.length > 0 && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <h3 className="font-medium text-gray-700 dark:text-gray-300">
                  {t('bulkStockTransfer.transferList', { count: manualEntries.length })}
                </h3>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {manualEntries.map((entry, index) => (
                  <div key={index} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">
                        {entry.username}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {entry.amount} {t('bulkStockTransfer.currency')}
                      </p>
                    </div>
                    <button
                      onClick={() => removeManualEntry(index)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 dark:text-white">
          <Users size={20} />
          {t('bulkStockTransfer.availableAgents', { count: availableDownlines.length })}
        </h2>
        
        {agentsLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {t('bulkStockTransfer.loadingAgents')}
            </p>
          </div>
        ) : availableDownlines.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableDownlines.map(agent => (
              <div key={agent.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <p className="font-medium text-gray-800 dark:text-white">
                  {agent.username}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {agent.email}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  {agent.mobileNumber}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {t('bulkStockTransfer.noAgentsFound')}
          </div>
        )}
      </div>

      <div className="bg-primary/5 dark:bg-[#ff6b6b]/10 border border-primary/20 dark:border-[#ff6b6b]/20 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-primary dark:text-[#ff6b6b] mb-3 flex items-center gap-2">
          <AlertCircle size={20} />
          {t('bulkStockTransfer.instructionsTitle')}
        </h3>
        <ul className="text-[#1d1d1d] dark:text-gray-300 space-y-2">
          <li>• {t('bulkStockTransfer.instruction1')}</li>
          <li>• {t('bulkStockTransfer.instruction2')}</li>
          <li>• {t('bulkStockTransfer.instruction3')}</li>
          <li>• {t('bulkStockTransfer.instruction4')}</li>
          <li>• {t('bulkStockTransfer.instruction5')}</li>
          <li>• {t('bulkStockTransfer.instruction6')}</li>
        </ul>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={bulkTransferMutation.isPending || 
            (transferMode === "excel" && !selectedFile) ||
            (transferMode === "manual" && manualEntries.length === 0)}
          className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-[#ac0303] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 dark:bg-[#ff6b6b] dark:hover:bg-[#ff5252]"
        >
          {bulkTransferMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {t('bulkStockTransfer.processing')}
            </>
          ) : (
            <>
              <Upload size={18} />
              {t('bulkStockTransfer.processTransfer')}
            </>
          )}
        </button>
      </div>

      {bulkTransferMutation.isPending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
              <h3 className="text-lg font-semibold dark:text-white">
                {t('bulkStockTransfer.processingTitle')}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              {t('bulkStockTransfer.processingMessage')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkStockTransfer;